import { t } from 'i18next';
import { EventActor, EventActorInfo } from '@stigg-types/apiTypes';
import { DateText, GridFlex, LongText, Text } from '@stigg-components';
import DetailsRow from './DetailsRow';
import EventActorIcon from '../EventActorIcon';
import { getActorTypeDisplayName, getActorUserInformation } from '../../utils/actor.utils';
import { Link, useLocation } from 'react-router-dom';
import { COLORS } from '../../../../theme/colors';
import moment from 'moment';

const LOCAL_IP_ADDRESSED = ['::1', '127.0.0.1'];

type EventActorSectionProps = {
  actor: EventActorInfo | null | undefined;
};

function ActorTypeRow({ actor }: { actor: EventActorInfo }) {
  const value = (
    <GridFlex.Row gap={2}>
      <EventActorIcon type={actor.type} />
      <Text.B2>{getActorTypeDisplayName(actor.type)}</Text.B2>
    </GridFlex.Row>
  );

  return <DetailsRow title={t('eventsLog.detailsDrawer.actor.type')} value={value} />;
}

function ActorUserRow({ actor }: { actor: EventActorInfo }) {
  const actorUserInformation = getActorUserInformation(actor);

  if (!actorUserInformation || actor.type === EventActor.Workflow) {
    return null;
  }

  return <DetailsRow title={t('eventsLog.detailsDrawer.actor.user')} value={actorUserInformation} />;
}

function ActorIpAddressRow({ actor }: { actor: EventActorInfo }) {
  if (!actor.ipAddress || actor.type === EventActor.Workflow) {
    return null;
  }

  const ipAddress = LOCAL_IP_ADDRESSED.includes(actor.ipAddress) ? 'localhost' : actor.ipAddress;

  return <DetailsRow title={t('eventsLog.detailsDrawer.actor.ipAddress')} value={ipAddress} />;
}

type TempEventActorInfo = EventActorInfo & {
  workflowExecutionId: string;
  workflowId: string;
  workflowName: string;
  workflowExecutionTime: string;
};

function getEnvironmentSlug(path: string) {
  const pathSegments = path.split('/');
  return pathSegments[1] || '';
}

function ActorWorkflowRow({ actor }: { actor: TempEventActorInfo }) {
  const location = useLocation();
  const environmentSlug = getEnvironmentSlug(location.pathname);

  if (!actor.workflowExecutionId) {
    return null;
  }

  const workflowLink = (
    <LongText maxLines={2}>
      <Link
        style={{ textDecoration: 'none', color: COLORS['primary.main'] }}
        to={`/${environmentSlug}/workflows?workflowId=${actor.workflowId}`}>
        {actor.workflowName}
      </Link>
    </LongText>
  );

  return <DetailsRow title={t('eventsLog.detailsDrawer.actor.workflow')} value={workflowLink} />;
}

function ActorWorkflowExecution({ actor }: { actor: TempEventActorInfo }) {
  const location = useLocation();
  const environmentSlug = getEnvironmentSlug(location.pathname);

  if (!actor.workflowExecutionId) {
    return null;
  }

  const title = t('eventsLog.detailsDrawer.actor.workflowExecution');
  const link = (
    <Link
      style={{ textDecoration: 'none', color: COLORS['primary.main'] }}
      to={`/${environmentSlug}/workflows?workflowId=${actor.workflowId}&workflowExecutionId=${actor.workflowExecutionId}`}>
      <DateText
        color="primary.main"
        date={moment.utc(actor.workflowExecutionTime).toDate()}
        withTime
        formatType="longDate"
      />
    </Link>
  );

  return (
    <GridFlex.Row>
      <Text.B2 minWidth={170}>{title}</Text.B2>
      <Text.B2>{link}</Text.B2>
    </GridFlex.Row>
  );
}

export default function EventActorSection({ actor }: EventActorSectionProps) {
  if (!actor) {
    return null;
  }

  return (
    <GridFlex.Column gap={4}>
      <Text.B2 $bold>{t('eventsLog.detailsDrawer.actor.title')}</Text.B2>
      <ActorTypeRow actor={actor} />
      <ActorUserRow actor={actor} />
      <ActorIpAddressRow actor={actor} />
      <ActorWorkflowRow actor={actor as TempEventActorInfo} />
      <ActorWorkflowExecution actor={actor as TempEventActorInfo} />
    </GridFlex.Column>
  );
}
